import { FC, useEffect, useState } from "react";
import { StockNumericType } from "data/enums";
import { stockDetailPath } from "data/stock_types";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import CommonLayout from "./CommonLayout";
import { useDispatch, useSelector } from "react-redux";
import Skeleton from "react-loading-skeleton";
import { useCreateExperienceMutation, useLazyGetExperienceQuery } from "application/features/experience/experienceApiSlice";
import { selectCurrentExperience, setExperience } from "application/features/experience/experienceSlice";
import MultiStockCard from "components/Quasi/MultiStockCard/MultiStockCard";
import type { Experience } from "data/types/experience";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";

export interface PageAddExperience5Props { }

const PageAddExperience5: FC<PageAddExperience5Props> = () => {

	const { t, ready } = useTranslation("translation");

	const experience = useSelector(selectCurrentExperience);
	const [experienceCard, setExperienceCard] = useState<Experience | null>(null);
	const [createExperience, { isLoading, isError }] = useCreateExperienceMutation();
	const [triggerGetQuery, { data: getExperience, isLoading: isLoadingExperience, isSuccess }] = useLazyGetExperienceQuery(experience?.experience_uuid)
	const [searchParams] = useSearchParams();
	const [error, setError] = useState<string>("");

	const dispatch = useDispatch();
	const navigate = useNavigate();
	const returnToParam = searchParams.get("returnTo");
	const isPackageFlowReturn = Boolean(
		returnToParam &&
		returnToParam.startsWith("/host/new-holiday-package/add-listing-"),
	);
	const returnHref = isPackageFlowReturn ? returnToParam : "/host/my-properties";
	const nextCtaText = isPackageFlowReturn ? "Torna al pacchetto" : "I tuoi annunci";

	const handleExperienceCreation = async (): Promise<void> => {
		try {
			const obj = {
				...experience
			};

			delete obj.host;

			const response = await createExperience(obj).unwrap();
			const updatedExperience = await triggerGetQuery(response.experience_uuid).unwrap();
			setExperienceCard(updatedExperience.experience);
			// Reset the experience in the Redux store
			dispatch(setExperience({}));
		} catch (err: any) {
			// Backend returns errors in two shapes:
			//   400 { message: "Missing required fields: name, cover, ..." }  — no code
			//   500 { message: <raw error.message> }                           — leaks the error
			// Surface the actual message whenever we can; fall back to a generic
			// one only when the backend sent nothing useful.
			const backendMessage = err?.data?.error?.message || err?.data?.message;
			setError(backendMessage || "Riprova nuovamente la procedura cliccando il pulsante sottostante.");
		}
	}

	const handleError = () => {
		dispatch(setExperience({}));
		navigate(returnHref);
	};

	// Trigger the experience creation when the component mounts
	useEffect(() => {
		if (experience) {
			handleExperienceCreation();
		}
	}, []);

	if (isError) {
		return (
			<CommonLayout
				nextBtnText={nextCtaText}
				index="4"
				nextHref={returnHref}
				canGoNext={true}
			>
				<>
					<div>
						<h2 className="text-2xl font-semibold">Si è verificato un errore!</h2>
						<span className="block mt-2 text-neutral-500 dark:text-neutral-400">
							{error || "Riprova nuovamente la procedura cliccando il pulsante sottostante."}
						</span>
					</div>
					<div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
					<div>
						<div className="flex items-center space-x-5 mt-8">
							<ButtonSecondary href={"mailto:help@tribyou.life"}>
								<span className="ml-3">Chiedi aiuto</span>
							</ButtonSecondary>
							<ButtonPrimary onClick={handleError}>
								<span className="ml-3">Riprova la procedura</span>
							</ButtonPrimary>
						</div>
					</div>
				</>
			</CommonLayout>
		)
	}

	if (!ready) return null;

	return (
		<CommonLayout
			nextBtnText={nextCtaText}
			index="4"
			nextHref={returnHref}
			canGoNext={true}
		>

			{
				(isLoading || isLoadingExperience) && (
					<div className="flex flex-1 items-center justify-center p-10 w-screen h-screen">
						<div className="md:w-1/3 w-full">
							<Skeleton className="w-full h-[30rem] md:h-[40rem]" />
						</div>
					</div>
				)
			}

			{(isSuccess && !isLoading && !isLoadingExperience && experienceCard) && (
				<>
					<div className="max-w-2xl mx-auto">
						<h2 className="text-2xl font-semibold">Congratulazioni 🎉</h2>
						<span className="block mt-2 text-neutral-500 dark:text-neutral-400">
							Il tuo annuncio è stato pubblicato con successo. Puoi visualizzarlo o modificarlo nella sezione "I miei annunci".
						</span>
					</div>
					<div className="w-14 border-b border-neutral-200 dark:border-neutral-700"></div>
					<div>
						<h3 className="text-lg font-semibold">Il tuo annuncio</h3>
						<div className="max-w-xs">
							<MultiStockCard
								id={experienceCard.experience_uuid}
								className="mt-8"
								cover={experienceCard.cover}
								images={experienceCard.gallery_images || experienceCard.media || []}
								title={experienceCard.name}
								description={experienceCard.description || ""}
								address={experienceCard.address || ""}
								price={experienceCard.price}
								max_guests={experienceCard.max_guests || 0}
								minutes_duration={experienceCard.minutes_duration || 0}
								rating={null}
								reviewCount={null}
								href={stockDetailPath(StockNumericType.EXPERIENCE, experienceCard.slug || experienceCard.experience_uuid)}
							/>
						</div>
						<div className="flex items-center space-x-5 mt-8">
							<ButtonSecondary href={"/host/edit-experience/" + experienceCard?.experience_uuid}>
								<svg
									xmlns="http://www.w3.org/2000/svg"
									className="h-5 w-5"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor"
								>
									<path
										strokeLinecap="round"
										strokeLinejoin="round"
										strokeWidth={1.5}
										d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
									/>
								</svg>
								<span className="ml-3">Modifica</span>
							</ButtonSecondary>
						</div>
					</div>
				</>
			)}
		</CommonLayout>
	);
};

export default PageAddExperience5;
